import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import SiteLayout from "@/components/site/SiteLayout";
import Seo from "@/components/site/Seo";
import { API } from "@/lib/api";
import { useCurrency, money } from "@/lib/currency";
import { track } from "@/lib/analytics";

export default function Checkout() {
  const { kind, id } = useParams();
  const { currency, country, switchToInr } = useCurrency();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", whatsapp: "", company: "", travellers: "", notes: "", coupon: "" });
  const [status, setStatus] = useState("idle");
  const [result, setResult] = useState(null);
  const [payQuote, setPayQuote] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isGuide = kind === "guide" || kind === "bundle";
  const gateway = currency === "inr" ? "Razorpay" : "Stripe";

  useEffect(() => {
    if (kind === "tour") {
      fetch(`${API}/cohorts`).then((r) => r.json()).then((cs) => setItem(cs.find((c) => c.id === id))).catch(() => {});
    } else {
      fetch(`${API}/guides`, { credentials: "include" }).then((r) => r.json()).then((d) => {
        const list = kind === "bundle" ? d.bundles : d.guides;
        setItem(list.find((x) => x.slug === id));
      }).catch(() => {});
    }
    const em = localStorage.getItem("atlas_email"); if (em) setForm((f) => ({ ...f, email: em }));
  }, [kind, id, currency]);

  useEffect(() => {
    if (!(kind === "guide" || kind === "bundle")) return;
    fetch(`${API}/payments/quote/${kind}/${id}`, { credentials: "include" })
      .then((r) => r.json()).then((d) => setPayQuote(d)).catch(() => {});
  }, [kind, id]);

  useEffect(() => {
    if (status !== "done" || !result?.order_number || !isGuide) return;
    const key = `atlas_purchase_event_${result.order_number}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    track("premium_guide_purchase", { order_number: result.order_number, item_type: kind, item_slug: id });
  }, [status, result, isGuide, kind, id]);

  if (!item) return <SiteLayout><div className="simplepage"><p>Loading…</p></div></SiteLayout>;

  const title = kind === "tour" ? `${item.tour} — ${item.name}` : (item.title || item.name);
  const price = kind === "tour" ? money(currency, currency === "inr" ? item.price_inr : item.price_usd) : (payQuote ? money(payQuote.currency, payQuote.amount) : "…");

  const loadRzp = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true); s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const purchase = async (e) => {
    e.preventDefault();
    if (!form.email || !form.email.includes("@")) { setStatus("error"); return; }
    setStatus("sending");
    try {
      const r = await fetch(`${API}/guides/razorpay/order`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ email: form.email, name: form.name, item_type: kind, item_slug: id, coupon: form.coupon }),
      });
      const d = await r.json();
      if (!r.ok) { setStatus("error"); alert(d.detail || "Could not start checkout"); return; }
      try { localStorage.setItem("atlas_email", form.email); } catch {}
      if (d.free) { setResult(d); setStatus("done"); return; }
      const ok = await loadRzp();
      if (!ok || !window.Razorpay) { setStatus("error"); alert("Could not load the payment gateway. Please try again."); return; }
      const rzp = new window.Razorpay({
        key: d.key_id, amount: d.amount, currency: d.currency, order_id: d.order_id,
        name: d.name, description: title, prefill: d.prefill, theme: { color: "#042898" },
        handler: async (resp) => {
          try {
            const vr = await fetch(`${API}/guides/razorpay/verify`, {
              method: "POST", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ razorpay_order_id: resp.razorpay_order_id, razorpay_payment_id: resp.razorpay_payment_id, razorpay_signature: resp.razorpay_signature }),
            });
            const vd = await vr.json();
            if (vr.ok && vd.ok) { setResult(vd); setStatus("done"); }
            else { setStatus("error"); alert(vd.detail || "Payment verification failed. If you were charged, contact us and we'll sort it out."); }
          } catch { setStatus("error"); }
        },
        modal: { ondismiss: () => setStatus("idle") },
      });
      rzp.on("payment.failed", () => { setStatus("error"); alert("Payment failed. Please try again."); });
      rzp.open();
    } catch { setStatus("error"); }
  };

  const bookTour = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.whatsapp) { setStatus("error"); return; }
    setStatus("sending");
    try {
      await fetch(`${API}/leads`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: form.name, email: form.email, whatsapp: form.whatsapp, company: form.company,
          category: "Canton Fair tour", description: `TOUR booking intent: ${title}. Travellers: ${form.travellers}. Notes: ${form.notes}`,
          source: "enquiry", currency }),
      });
      navigate("/booking-confirmed");
    } catch { setStatus("error"); }
  };

  if (status === "done" && result) {
    return (
      <SiteLayout>
        <Seo title="Order complete — The Sourcing Atlas" description="Your guides are ready." path="/checkout" noindex />
        <div className="simplepage" data-testid="checkout-success">
          <h1>✓ Thank you — order {result.order_number}</h1>
          <p style={{ color: "var(--grey)" }}>We've emailed your downloads to <strong>{form.email}</strong>. Each link works for 30 days.</p>
          <div style={{ display: "grid", gap: 10, marginTop: 16 }}>
            {(result.downloads || []).map((l) => (
              <a key={l.slug} href={l.url} className="btn btn-line" data-testid={`buy-dl-${l.slug}`} style={{ textAlign: "left", padding: "11px 15px" }}>⬇ {l.title}</a>
            ))}
          </div>
          <p style={{ marginTop: 16 }}><Link className="btn btn-navy" to="/guides/my-library">Go to my library</Link></p>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <Seo title={`Checkout — ${title} | The Sourcing Atlas`} description="Secure checkout for tour bookings and digital guides." path="/checkout" noindex />
      <div className="crumb"><Link to="/">Home</Link> › Checkout</div>
      <div className="simplepage" data-testid="checkout-page">
        <div style={{ background: "#EAF1FB", border: "1px solid #9DBBEB", borderRadius: 10, padding: "12px 16px", marginBottom: 20, fontSize: 13.5, color: "#0B2649" }}>
          Secure payment via <strong>Razorpay</strong> — cards, UPI, netbanking &amp; wallets. Your guides unlock <strong>only after payment succeeds</strong>.
        </div>
        <h1>{title}</h1>
        <p style={{ fontSize: 22, fontWeight: 800, color: "var(--navy)" }} data-testid="checkout-price">
          {price} {kind === "tour" && <small style={{ fontSize: 13, color: "var(--grey)" }}>/ person</small>}
        </p>
        {isGuide && (
          <p style={{ fontSize: 13, color: "var(--grey)" }}>Billed via Razorpay in {payQuote ? payQuote.currency.toUpperCase() : "your local currency"}.</p>
        )}

        {isGuide ? (
          <form onSubmit={purchase} style={{ marginTop: 20, maxWidth: 520 }}>
            <input placeholder="Full name" value={form.name} onChange={set("name")} style={ci} data-testid="co-name" />
            <input placeholder="Email (this is your licence)" value={form.email} onChange={set("email")} style={ci} data-testid="co-email" />
            <input placeholder="Coupon code (optional)" value={form.coupon} onChange={set("coupon")} style={ci} data-testid="co-coupon" />
            {status === "error" && <p style={{ color: "#D62828", fontSize: 13 }}>A valid email is required.</p>}
            <button className="btn btn-red" style={{ width: "100%" }} disabled={status === "sending"} data-testid="co-submit">
              {status === "sending" ? "Opening secure payment…" : `Pay ${price} securely`}
            </button>
          </form>
        ) : (
          <form onSubmit={bookTour} style={{ marginTop: 20, maxWidth: 520 }}>
            <input placeholder="Full name" value={form.name} onChange={set("name")} style={ci} data-testid="co-name" />
            <input placeholder="Email" value={form.email} onChange={set("email")} style={ci} data-testid="co-email" />
            <input placeholder="WhatsApp number" value={form.whatsapp} onChange={set("whatsapp")} style={ci} data-testid="co-whatsapp" />
            <input placeholder="Company (optional)" value={form.company} onChange={set("company")} style={ci} />
            <input placeholder="Traveller names (as per passport)" value={form.travellers} onChange={set("travellers")} style={ci} />
            <textarea placeholder="Notes" value={form.notes} onChange={set("notes")} style={{ ...ci, minHeight: 80 }} />
            {status === "error" && <p style={{ color: "#D62828", fontSize: 13 }}>Name, email and WhatsApp are required.</p>}
            <button className="btn btn-red" style={{ width: "100%" }} disabled={status === "sending"} data-testid="co-submit">
              {status === "sending" ? "Submitting…" : `Continue to ${gateway}`}
            </button>
          </form>
        )}
        <p style={{ color: "var(--grey)", fontSize: 13, marginTop: 12 }}><Link to="/refund-policy" data-testid="refund-link">Refund &amp; cancellation policy</Link></p>
      </div>
    </SiteLayout>
  );
}
const ci = { width: "100%", border: "1.5px solid var(--line)", borderRadius: 10, padding: "12px 13px", fontSize: 14, marginBottom: 11, fontFamily: "inherit", background: "#fff" };
